import dayjs from "dayjs";
import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetBookings } from "~/services/booking";
import { useBooking } from "~/store";
import { capitalize } from "~/utils";
import { JobStatus, METADATA_DEFAULT, ServiceType } from "~/utils/constants";
import {
  BookingCalendarView,
  BookingFilters,
  BookingHeader,
  BookingList,
  BookingSearch,
} from "./components";
import { MetaData } from "~/types/common";

// Helper to get booking status - prioritizes actual database status
const getBookingStatus = (actualStatus?: string, serviceTickets?: any[]): string => {
  // PRIORITY 1: Use the actual booking status field if it exists
  // This ensures consistency with detail view and backend status cascade logic
  if (actualStatus) {
    // Special handling: if status is DRAFT but has service tickets, recalculate for legacy data
    if (actualStatus === "DRAFT" && serviceTickets && serviceTickets.length > 0) {
      // Continue to legacy calculation below
    } else {
      // Use the actual status for ESTIMATED, SCHEDULED, IN-PROGRESS, COMPLETED
      return actualStatus;
    }
  }

  // LEGACY: Calculate from timeIn/timeOut for older data that might not have proper status
  if (!serviceTickets || serviceTickets.length === 0) {
    return "DRAFT";
  }

  let completedCount = 0;
  let inProgressCount = 0;

  serviceTickets.forEach((ticket: any) => {
    if (ticket.timeOut || ticket.completedAt || ticket.completed_at) {
      completedCount++;
    } else if (ticket.timeIn) {
      inProgressCount++;
    }
  });

  if (completedCount === serviceTickets.length && serviceTickets.length > 0) {
    return "COMPLETED";
  }
  if (completedCount > 0 || inProgressCount > 0) {
    return "IN-PROGRESS";
  }
  return "SCHEDULED";
};

export const ListBooking = () => {
  const { filters, setFilters } = useBooking();

  // Pass all filters including bookingStatus to backend for proper pagination
  const { data, isLoading } = useGetBookings(filters);

  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState<MetaData>(METADATA_DEFAULT);

  // UI state
  const [viewMode, setViewMode] = useState(searchParams.get("view") || "list");
  const [showFilters, setShowFilters] = useState(
    Boolean(searchParams.get("showFilters"))
  );

  // Update meta from data whenever it changes
  useEffect(() => {
    if (data?.meta) {
      setMeta(data.meta);
    }
  }, [data?.meta]);


  // Load filters from URL on component mount
  useEffect(() => {
    const initialFilters: Record<string, any> = {};

    // Get filters from URL
    const search = searchParams.get("search");
    const page = searchParams.get("page");
    const clientId = searchParams.get("clientId");
    const serviceType = searchParams.get("serviceType");
    const bookingStatus = searchParams.get("status");
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");
    const timeFilter = searchParams.get("timeFilter");

    // Apply filters if they exist in URL
    if (search) initialFilters.search = search;
    if (page && !isNaN(Number(page))) initialFilters.page = Number(page);
    if (clientId) initialFilters.clientId = clientId;

    if (
      serviceType &&
      Object.values(ServiceType).includes(serviceType as ServiceType)
    ) {
      initialFilters.serviceType = serviceType;
    }

    if (
      bookingStatus &&
      Object.values(JobStatus).includes(bookingStatus as JobStatus)
    ) {
      initialFilters.bookingStatus = bookingStatus;
    }

    if (startDate) initialFilters.startDate = startDate;
    if (endDate) initialFilters.endDate = endDate;

    if (timeFilter && ["upcoming", "past"].includes(timeFilter)) {
      initialFilters.timeFilter = timeFilter;
    }

    // Apply filters if any were found in URL
    if (Object.keys(initialFilters).length > 0) {
      setFilters(initialFilters);
    }
  }, [searchParams, setFilters]);

  // Update URL when filters change
  useEffect(() => {
    if (!filters) return;

    setSearchParams(
      (params) => {
        // View mode
        if (viewMode !== "list") {
          params.set("view", viewMode);
        } else {
          params.delete("view");
        }

        // Show filters
        if (showFilters) {
          params.set("showFilters", "true");
        } else {
          params.delete("showFilters");
        }

        // Search
        if (filters.search) {
          params.set("search", filters.search);
        } else {
          params.delete("search");
        }

        // Pagination
        if (filters.page && filters.page > 1) {
          params.set("page", String(filters.page));
        } else {
          params.delete("page");
        }

        // Client
        if (filters.clientId) {
          params.set("clientId", filters.clientId);
        } else {
          params.delete("clientId");
        }

        // Service type
        if (filters.serviceType) {
          params.set("serviceType", filters.serviceType);
        } else {
          params.delete("serviceType");
        }

        // Booking status
        if (filters.bookingStatus) {
          params.set("status", filters.bookingStatus);
        } else {
          params.delete("status");
        }

        // Date filters
        if (filters.startDate) {
          params.set("startDate", filters.startDate);
        } else {
          params.delete("startDate");
        }

        if (filters.endDate) {
          params.set("endDate", filters.endDate);
        } else {
          params.delete("endDate");
        }

        // Time filter
        if (filters.timeFilter) {
          params.set("timeFilter", filters.timeFilter);
        } else {
          params.delete("timeFilter");
        }

        return params;
      },
      { replace: true }
    );
  }, [filters, viewMode, showFilters, setSearchParams]);

  // Handle view mode change with URL update
  const handleViewModeChange = (mode: string) => {
    setViewMode(mode);
  };

  // Handle show filters change with URL update
  const handleShowFiltersChange = (show: boolean) => {
    setShowFilters(show);
  };

  const { displayedBookings } = useMemo(() => {
    if (!data?.items) return { displayedBookings: [] };

    // Map and get correct status for each booking
    const mappedBookings = data.items.map((booking) => {
      const serviceTickets = booking.serviceTicket || [];
      // Use the actual booking status, falling back to calculation only if needed
      const bookingStatus = getBookingStatus(booking.status, serviceTickets);

      // Calculate total: prioritize saved invoiceConfig grandTotal (from billing tab),
      // fall back to payment.total, otherwise 0
      const estimateTotal = booking.invoiceConfig?.grandTotal ?? booking.payment?.total ?? 0;

      return {
        id: booking.id,
        referenceNumber: booking.referenceNumber,
        // Client column should ALWAYS show the booking client, not shipper/consignee from service tickets
        client:
          booking.client?.companyName ||
          [booking.client?.firstName, booking.client?.lastName]
            .filter(Boolean)
            .join(" ")
            .trim() ||
          // Only fall back to tempClientData for quick bookings with no actual client
          booking.tempClientData?.name ||
          "Unnamed Client",
        isQuickClient: !booking.client && !!booking.tempClientData,
        isAppBooking: (() => {
          // Check if any service ticket has staffNotes starting with "=== CLIENT SERVICE REQUEST ==="
          return serviceTickets.some((ticket: any) => 
            ticket.staffNotes && ticket.staffNotes.includes("=== CLIENT SERVICE REQUEST ===")
          );
        })(),
        project: booking.project?.name,
        sidemark: booking.sidemark,
        date: dayjs(
          booking.bookingDate ?? booking.serviceTicket[0]?.startDatetime
        ).format("YYYY-MM-DD"),
        status: bookingStatus,
        paymentStatus: "pending",
        totalAmount: estimateTotal,
        description: (() => {
          // Count service types
          const serviceTypeCounts = serviceTickets.reduce((acc: Record<string, number>, ticket: any) => {
            const type = ticket.serviceType;
            acc[type] = (acc[type] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);

          // Create description with counts
          return Object.entries(serviceTypeCounts)
            .map(([type, count]) => `${count} ${capitalize(type)}`)
            .join(", ");
        })(),
        inventory:
          booking.serviceTicket?.reduce<Array<{ id: string; title: string }>>(
            (acc, ticket) => {
              // Use a Map to track unique inventory items
              const uniqueInventoryMap = new Map(
                acc.map((item) => [item.id, item])
              );

              // Add new items from this ticket if they don't already exist
              ticket.inventories?.forEach((item: any) => {
                if (!uniqueInventoryMap.has(item.id)) {
                  // Use title, fallback to artistName, then inventoryRef, then "Untitled"
                  const displayTitle = item.title || item.artistName || item.inventoryRef || "Untitled";
                  uniqueInventoryMap.set(item.id, {
                    id: item.id,
                    title: displayTitle,
                  });
                }
              });

              // Convert Map back to array
              return Array.from(uniqueInventoryMap.values());
            },
            []
          ) || [],
        // Include original booking data for container information
        originalBooking: booking,
        serviceTickets: serviceTickets,
      };
    });

    return {
      displayedBookings: mappedBookings,
    };
  }, [data?.items]);

  return (
    <div className="bg-gray-50">
      {/* Header Component */}
      <BookingHeader />

      {/* Search and Filter Components */}
      <BookingSearch
        showFilters={showFilters}
        setShowFilters={handleShowFiltersChange}
        viewMode={viewMode}
        setViewMode={handleViewModeChange}
        newLeadsCount={(data?.meta as any)?.newLeadsCount ?? 0}
      />

      <BookingFilters showFilters={showFilters} />

      {/* Content View - List or Calendar */}
      {viewMode === "list" ? (
        <BookingList
          isLoading={isLoading}
          bookings={displayedBookings || []}
          filters={filters}
          setFilters={setFilters}
          totalItems={meta.totalItems}
        />
      ) : (
        <BookingCalendarView
          setViewMode={handleViewModeChange}
          bookings={displayedBookings || []}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
